import { useEffect, useState } from "react";
import { Steps } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../../utils/onboardingI18n";
import AccountInformation from "./components/AccountInformation";
import FinancialInfo from "./components/FinancialInfo";
import Declaration from "./components/Declaration";
import EKYC from "./components/EKYC";
import ApplicationStatus from "./components/ApplicationStatus";
import Loader from "../../../components/Loader";
import { getIndividualAccount } from "../../../api/request/individualOnboarding";
import { CheckKycStatusApi } from "../../../api/request/kyc";
import { setIndividualOnboarding } from "../../../store/Slices/individualSlice";
import { setUserDetails } from "../../../store/Slices/userSlice";
import { useResponsive } from "../../../hooks/useResponsive";

const { Step } = Steps;

const IndividualOnboardingSteps = () => {
  const { t } = useTranslation("onBoarding");
  const dispatch = useDispatch();
  const { isMobile } = useResponsive();
  const { userDetails } = useSelector((state) => state.user);
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState(null);
  const [completedStep, setCompletedStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const kycInProgress = userDetails?.kycStatus === "Pending";
  const isSubmitted = completedStep >= 4 || kycInProgress;

  const syncAccountProgress = async ({
    showLoader = false,
    preserveCurrentStep = false,
  } = {}) => {
    if (showLoader) setIsLoading(true);
    try {
      const { data } = await getIndividualAccount();
      if (data?.success) {
        const accountData = data?.data;
        setData(accountData);

        if (!preserveCurrentStep) {
          setCurrentStep(accountData?.currentStep);
          setCompletedStep(accountData?.currentStep - 1);
        } else {
          setCompletedStep((prev) =>
            Math.max(prev, (accountData?.currentStep ?? 1) - 1)
          );
        }

        dispatch(
          setIndividualOnboarding({
            status: accountData?.status,
            isApplied: accountData?.isApplied,
            currentStep: accountData?.currentStep,
            completedPercentage: accountData?.completedPercentage,
          })
        );
      }
    } catch (error) {
    } finally {
      if (showLoader) setIsLoading(false);
    }
  };

  useEffect(() => {
    syncAccountProgress({ showLoader: true });
  }, []);

  useEffect(() => {
    const userId = userDetails?._id || userDetails?.id;
    if (!userId) return;

    let cancelled = false;

    const loadKycStatus = async () => {
      try {
        const params = new URLSearchParams({ UserId: userId }).toString();
        const { data } = await CheckKycStatusApi(params);
        if (cancelled || !data?.success) return;

        const kycStatus = data?.data?.kycStatus;
        if (kycStatus && kycStatus !== userDetails?.kycStatus) {
          dispatch(setUserDetails({ ...userDetails, kycStatus }));
        }
      } catch (error) {}
    };

    loadKycStatus();

    return () => {
      cancelled = true;
    };
  }, [userDetails?._id, userDetails?.id]);

  const onNext = () => {
    setCurrentStep((prev) => {
      setCompletedStep((completed) => Math.max(completed, prev));
      return prev + 1;
    });
    syncAccountProgress({ preserveCurrentStep: true });
  };

  const onPrevious = () => setCurrentStep((prev) => prev - 1);

  const handleStepClick = (step) => {
    if (step > completedStep + 1) return;
    setCurrentStep(step);
  };

  const steps = [
    {
      key: 1,
      title: t("Account Information"),
      content: (
        <AccountInformation
          onNext={onNext}
          onPrevious={onPrevious}
          isSubmitted={isSubmitted}
          id={data?.personalInfoId}

        />
      ),
    },
    {
      key: 2,
      title: t("Financial Info"),
      content: (
        <FinancialInfo
          onNext={onNext}
          onPrevious={onPrevious}
          isSubmitted={isSubmitted}
          id={data?.employeeFinancialInfoId}
        />
      ),
    },
    {
      key: 3,
      title: t("Declaration"),
      content: (
        <Declaration
          onNext={onNext}
          onPrevious={onPrevious}
          isSubmitted={isSubmitted}
          // declarationInfo={data?.declarationInfo}
        />
      ),
    },
    {
      key: 4,
      title: t("EKYC"),
      content: (
        <EKYC
          onNext={onNext}
          onPrevious={onPrevious}
          isSubmitted={isSubmitted}
          id={data?.kycId}
        />
      ),
    },
    {
      key: 5,
      title: t("Application Status"),
      content: <ApplicationStatus id={data?.applicationStatusId} />,
    },
  ];

  if (isLoading) return <Loader />;

  return (
    <div className="onboarding-form w-full min-w-0">
      <div className="overflow-x-auto sticky top-0 z-20 border-b border-gray-100 bg-white py-5">
        <div className={isMobile ? "w-full" : "min-w-[720px] sm:min-w-0"}>
          <Steps
            current={currentStep - 1}
            size="small"
            direction="horizontal"
            responsive={false}
            className={isMobile ? "[&_.ant-steps-item-content]:hidden" : undefined}
          >
            {steps.map((step) => (
              <Step
                key={step?.key}
                title={isMobile ? undefined : step?.title}
                className="cursor-pointer"
                onClick={() => handleStepClick(step?.key)}
              />
            ))}
          </Steps>
        </div>
      </div>

      <div className="min-h-[400px]">{steps[currentStep - 1]?.content}</div>
    </div>
  );
};

export default IndividualOnboardingSteps; 